import * as React from 'react';
import type {AttributesObject} from '@packages/systems/wfdl';
import type {CodeIslandSize} from './CodeIslandSharedUtils';

const ElementCodeCompIcon = () => (
  <svg
    data-wf-icon="ElementCodeCompIcon"
    width="34"
    height="34"
    viewBox="0 0 34 34"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M24.1262 19.9344L20.6274 23.4332L24.1262 26.9319L22.6238 28.4344L17.6226 23.4332L22.6238 18.4319L24.1262 19.9344Z"
      fill="currentColor"
    />
    <path
      d="M33.3774 23.4332L28.3762 28.4344L26.8738 26.9319L30.3726 23.4332L26.8738 19.9344L28.3762 18.4319L33.3774 23.4332Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.8828 3.5943C13.5171 3.24834 14.2843 3.24836 14.9186 3.5943L23.4124 8.22821C24.8889 9.03362 24.8889 11.154 23.4124 11.9594L14.9642 16.5684V25.8342C14.9642 27.4472 13.2364 28.4722 11.8203 27.6998L3.23315 23.016C2.55057 22.6437 2.12512 21.928 2.125 21.1504V10.0938C2.125 9.70499 2.33774 9.34616 2.67908 9.15997L12.8828 3.5943ZM4.25 21.1504L12.8392 25.8342V16.5684L4.25 11.8826V21.1504ZM5.40588 10.0938L13.9017 14.7277L22.3955 10.0938L13.9017 5.4599L5.40588 10.0938Z"
      fill="currentColor"
    />
  </svg>
);

// SMIL rotation — CSS keyframes can't cross the shadow-DOM boundary
const WaitIcon = () => (
  <svg
    data-wf-icon="WaitIcon"
    width="24"
    height="24"
    viewBox="0 0 30 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g>
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 15 15"
        to="360 15 15"
        dur="0.6s"
        repeatCount="indefinite"
      />
      <path
        opacity="0.2"
        d="M15 30a15 15 0 110-30 15 15 0 010 30zm0-3a12 12 0 100-24 12 12 0 000 24z"
        fill="currentColor"
      />
      <path
        opacity="0.75"
        d="M0 15A15 15 0 0115 0v3A12 12 0 003 15H0z"
        fill="currentColor"
      />
    </g>
  </svg>
);

const RefreshIcon = () => (
  <svg
    data-wf-icon="RefreshIcon"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.5 8C13.5 4.96243 11.0376 2.5 8 2.5C6.76241 2.5 5.61898 2.90936 4.69971 3.59985L5.30029 4.39942C6.05234 3.83453 6.98638 3.5 8 3.5C10.4853 3.5 12.5 5.51472 12.5 8V8.79298L10.8535 7.14649L10.1464 7.8536L13 10.7072L15.8535 7.8536L15.1464 7.14649L13.5 8.7929V8Z"
      fill="currentColor"
    />
    <path
      d="M3.5 7.2071L5.14641 8.85351L5.85352 8.1464L2.99996 5.29285L0.146409 8.1464L0.853516 8.85351L2.5 7.20702V8C2.5 11.0376 4.96243 13.5 8 13.5C9.23759 13.5 10.381 13.0906 11.3003 12.4001L10.6997 11.6006C9.94766 12.1655 9.01362 12.5 8 12.5C5.51472 12.5 3.5 10.4853 3.5 8V7.2071Z"
      fill="currentColor"
    />
  </svg>
);

const CopyIcon = () => (
  <svg
    data-wf-icon="CopyIcon"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 3C6 2.44772 6.44772 2 7 2H13C13.5523 2 14 2.44772 14 3V9C14 9.55228 13.5523 10 13 10H7C6.44772 10 6 9.55228 6 9V3ZM7 3H13V9H7V3Z"
      fill="currentColor"
    />
    <path
      d="M3 5V12C3 12.5523 3.44772 13 4 13H11V12H4V5H3Z"
      fill="currentColor"
    />
  </svg>
);

const CircleXIcon = ({size = 48}: {size?: number}) => (
  <svg
    data-wf-icon="CircleXIcon"
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="24"
      cy="24"
      r="17"
      stroke="rgba(255,255,255,0.25)"
      strokeWidth="1.5"
    />
    <circle
      cx="24"
      cy="24"
      r="21"
      stroke="rgba(255,255,255,0.12)"
      strokeWidth="1"
    />
    <path
      d="M18 18L30 30M30 18L18 30"
      stroke="rgba(255,255,255,0.55)"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const AISparkleSmallIcon = () => (
  <svg
    data-wf-icon="AISparkleSmallIcon"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2 8.5V7.5C5.03757 7.5 7.5 5.03757 7.5 2H8.5C8.5 5.03757 10.9624 7.5 14 7.5V8.5C10.9624 8.5 8.5 10.9624 8.5 14H7.5C7.5 10.9624 5.03757 8.5 2 8.5ZM8 11.4956C8.65852 9.91976 9.91976 8.65852 11.4956 8C9.91976 7.34148 8.65852 6.08024 8 4.5044C7.34148 6.08024 6.08024 7.34148 4.5044 8C6.08024 8.65852 7.34148 9.91976 8 11.4956Z"
      fill="currentColor"
    />
  </svg>
);

export function CodeIslandStateCardFixAction({onClick}: {onClick: () => void}) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      data-automation-id="code-island-fix-with-ai-button"
      style={{
        background: 'transparent',
        color: 'currentcolor',
        border: '1px solid',
        borderColor: hovered
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(255, 255, 255, 0.13)',
        borderStyle: 'solid',
        borderRadius: 4,
        padding: '4px 8px',
        lineHeight: '16px',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          width: 16,
          height: 16,
          marginRight: 6,
          color: 'rgba(255, 255, 255, 0.67)',
        }}
      >
        <AISparkleSmallIcon />
      </div>
      Fix all
    </button>
  );
}

export function CodeIslandStateCardCopyAction({
  errorMessage,
}: {
  errorMessage: string;
}) {
  const [hovered, setHovered] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const handleClick = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(errorMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers or non-HTTPS
      const textArea = document.createElement('textarea');
      textArea.value = errorMessage;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [errorMessage]);

  return (
    <button
      data-automation-id="code-island-copy-button"
      style={{
        background: 'transparent',
        color: 'currentcolor',
        border: 1,
        borderColor: hovered
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(255, 255, 255, 0.13)',
        borderStyle: 'solid',
        borderRadius: 4,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 8,
        paddingRight: 8,
        display: 'flex',
        alignItems: 'center',
      }}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          width: 16,
          height: 16,
          marginRight: 4,
          color: 'rgba(255, 255, 255, 0.67)',
        }}
      >
        <CopyIcon />
      </div>
      {copied ? 'Copied' : 'Copy error'}
    </button>
  );
}

export function CodeIslandStateCardReloadAction({
  onClick,
}: {
  onClick: () => void;
}) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      data-automation-id="code-island-reload-button"
      style={{
        background: 'transparent',
        color: 'currentcolor',
        border: 1,
        borderColor: hovered
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(255, 255, 255, 0.13)',
        borderStyle: 'solid',
        borderRadius: 4,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 8,
        paddingRight: 8,
        display: 'flex',
        alignItems: 'center',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          width: 16,
          height: 16,
          marginRight: 4,
          color: 'rgba(255, 255, 255, 0.67)',
        }}
      >
        <RefreshIcon />
      </div>
      Reload
    </button>
  );
}

export default function CodeIslandStateCard({
  children,
  type,
  action,
  attrs,
  layout = 'horizontal',
  dimensions,
}: {
  children: React.ReactNode;
  type?: 'error' | 'loading';
  action?: React.ReactNode;
  attrs?: AttributesObject;
  layout?: 'horizontal' | 'vertical';
  // Last rendered size of the component the card stands in for
  dimensions?: CodeIslandSize | null;
}) {
  // The loading card is always a vertically stacked, centered layout
  const isLoading = type === 'loading';
  const isHorizontal = !isLoading && layout === 'horizontal';

  return (
    <div
      data-code-island-state={type}
      data-automation-id={`code-island-${type}`}
      data-wf-code-island-error={type === 'error' ? 'true' : undefined}
      {...attrs}
      style={{
        ...attrs?.style,
        background: '#4D4D4D',
        fontFamily:
          "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif",
        fontSize: '11.5px',
        lineHeight: '16px',
        letterSpacing: isLoading ? '-0.115px' : undefined,
        fontWeight: 400,
        color: 'white',
        display: 'flex',
        flexDirection: isHorizontal ? 'row' : 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 75,
        paddingLeft: 8,
        paddingRight: 8,
        gap: isHorizontal ? 12 : isLoading ? 15 : 0,
        ...(dimensions
          ? {
              // The height prevents the layout shift; the width spans the
              // container (the recorded one may be from another breakpoint)
              width: '100%',
              height: dimensions.height,
              minHeight: 0,
              maxHeight: '100vh',
              boxSizing: 'border-box' as const,
              overflow: 'hidden' as const,
            }
          : null),
      }}
    >
      {type === 'error' ? (
        <CircleXIcon size={isHorizontal ? 48 : 64} />
      ) : (
        <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
          <WaitIcon />
          <ElementCodeCompIcon />
        </div>
      )}
      <div
        data-automation-id={`code-island-${type}-message`}
        style={{
          marginTop: isHorizontal || isLoading ? 0 : 12,
          marginBottom: isHorizontal || isLoading ? 0 : 8,
        }}
      >
        {children}
      </div>
      {action}
    </div>
  );
}
